import {
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import React, { useEffect } from "react";
import { Contact, ElectricityBlockValues } from "../types";
import { Field, Form, Formik, FormikErrors } from "formik";
import GeneralTextField from "./GeneralTextField";
import { serviceProviderValidationSchema } from "./Validations";
import FormContext from "src/context/FormContext";
import { apiClient } from "src/api/apiService";
import { mapMeterProviderValueToLabel } from "./MeterProvider";
import styled from "styled-components";

interface IAddContactProps {
  label: string;
  name: "licensedElectrician" | "asp";
  contactType: "ELECTRICIAN" | "SERVICE_PROVIDER";
  closeDialog: () => void;
  selectedContact?: Contact;
  fetchContacts: () => Promise<void>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<ElectricityBlockValues>>;
  meterProvider: string;
}

interface ContactASP extends Partial<Contact> {
  meterProvider: string;
}
const initialContact: Partial<ContactASP> = {
  first_name: "",
  id: "",
  last_name: "",
  license_number: "",
};

const StyledText = styled(Text)`
  font-size: 17px;
`;

const AddContactASP: React.FC<IAddContactProps> = ({
  closeDialog,
  selectedContact,
  fetchContacts,
  setFieldValue,
  contactType,
  name,
  label,
  meterProvider,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [contact, setContact] = React.useState<ContactASP>({
    ...initialContact,
    meterProvider,
  });
  const {
    formState: { msaNumber },
  } = React.useContext(FormContext);

  useEffect(() => {
    if (selectedContact) setContact({ ...selectedContact, meterProvider });
  }, []);

  const onAdd = async (values: ContactASP) => {
    setLoading(true);
    try {
      const {
        data: { id },
      } = await apiClient.post(`/contact/${msaNumber}`, {
        first_name: values.first_name,
        last_name: values.last_name,
        license_number:
          (meterProvider === "ACUMENMPB" ? "IH#" : "FSP") +
          values.license_number,
        meter_provider: meterProvider,
        contact_type: contactType,
        phone_number: "",
        email: "",
      });
      setLoading(false);
      closeDialog();
      await fetchContacts();
      setFieldValue(name, id);
    } catch (error) {
      console.log(`Add ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  const onEdit = async (values: ContactASP) => {
    setLoading(true);
    try {
      await apiClient.put(`/contact/${msaNumber}`, {
        id: values.id,
        first_name: values.first_name,
        last_name: values.last_name,
        license_number:
          (meterProvider === "ACUMENMPB" ? "IH#" : "FSP") +
          values.license_number,
        //TODO: check what to do with this field
        meter_provider: meterProvider,
        contact_type: contactType,
        phone_number: "",
        email: "",
      });
      setLoading(false);
      closeDialog();
      await fetchContacts();
    } catch (error) {
      console.log(`Edit ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  const onDelete = async () => {
    setLoading(true);
    try {
      await apiClient.put(`/contact/${selectedContact?.id}/deactivate`);
      setLoading(false);
      closeDialog();
      await fetchContacts();
      setFieldValue(name, "");
    } catch (error) {
      console.log(`Delete ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  return (
    <Dialog
      id="add-contact-asp-dialog"
      open={true}
      title={selectedContact ? `Edit ${label}` : `Add new ${label}`}
      onClose={closeDialog}
    >
      <Formik<ContactASP>
        validationSchema={serviceProviderValidationSchema}
        initialValues={
          selectedContact ? { ...selectedContact, meterProvider } : contact
        }
        onSubmit={(values) => {
          if (selectedContact) {
            onEdit(values);
          } else {
            onAdd(values);
          }
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <Form>
          <Stack space="small">
            {/* Box hack to fix issue with TextField label being cropped by Dialog title */}
            <Box />
            {/* End hack */}
            {/* Form fields */}
            <Stack space="xlarge">
              <Columns
                collapseBelow="sm"
                space={{
                  xs: "xlarge",
                  lg: "large",
                }}
              >
                <Column>
                  <Field
                    component={GeneralTextField}
                    name="first_name"
                    label="First name"
                  />
                </Column>
                <Column>
                  <Field
                    component={GeneralTextField}
                    name="last_name"
                    label="Last name"
                  />
                </Column>
              </Columns>
              <Field
                component={GeneralTextField}
                name="license_number"
                label="Accreditation number"
                startIcon={
                  <Box marginTop="none">
                    <StyledText>
                      {meterProvider === "ACUMENMPB" ? "IH#" : "FSP"}
                    </StyledText>
                  </Box>
                }
                helperText={
                  meterProvider === "ACUMENMPB"
                    ? "Electrician's accreditation number e.g. IH#12345"
                    : "Electrician's accreditation number e.g. FSP123"
                }
              />
              <TextField
                id="meter-provider-readonly"
                label="Meter provider"
                value={mapMeterProviderValueToLabel[meterProvider]}
                // @ts-expect-error: ODS seems not have this prop
                InputProps={{
                  readOnly: true,
                }}
              />
            </Stack>
            {/* End Form fields */}

            {/* Actions */}
            <Box
              position={{
                lg: "static",
                xs: "fixed",
              }}
              paddingTop={{
                lg: "medium",
                xs: "medium",
              }}
              paddingBottom={{
                lg: "none",
                xs: "medium",
              }}
              paddingX={{
                lg: "none",
                xs: "xlarge",
              }}
              backgroundColor="white"
              width="full"
              style={{
                left: "0",
                bottom: "0",
                zIndex: "1",
                //borderTop: "1px solid #E3E3E3",
              }}
              display={selectedContact ? "flex" : "block"}
              justifyContent="space-between"
            >
              {selectedContact && (
                <Button variant="text" color="primary" onClick={onDelete}>
                  Delete
                </Button>
              )}
              <Box
                display="flex"
                // flexWrap={{
                //   lg: "noWrap",
                //   xs: "wrap",
                // }}
                justifyContent="flex-end"
                alignItems="center"
              >
                <Box marginRight="large">
                  <Button variant="text" color="primary" onClick={closeDialog}>
                    Cancel
                  </Button>
                </Box>
                <Button type="submit" spinner={loading}>
                  Save person's data
                </Button>
              </Box>
            </Box>
            {/* End Actions */}
          </Stack>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default AddContactASP;
